import { Meteor } from 'meteor/meteor';
import { Roles } from 'roles';
import { ValidatedMethod } from 'meteor/mdg:validated-method';
import { SimpleSchema } from 'meteor/aldeed:simple-schema';
import { DDPRateLimiter } from 'meteor/ddp-rate-limiter';
import _ from 'lodash';
import moment from 'moment';

import { Places } from './places';
import { Flights } from './flights';
import { Montgolfieres } from '../montgolfieres/montgolfieres';
import { Reservations } from '../reservations/reservations';

import { SmsManager } from '../../external_api/smsManager';

export const prepareFlight = new ValidatedMethod({
	name: 'flights.prepare',
	validate: new SimpleSchema({
		_id: { type: String },
		place: { type: String, label: "lieu", allowedValues: ["anjou", "nantes"] },
		reservationIds: { type: [String], label: "réservations", minCount: 1 },
		pilotId: { type: String, label: "pilote", optional: true },
		montgolfiereId: { type: String, label: "montgolfiere" },
		temp: { type: Number }
	}).validator(),
	run({ _id, reservationIds, place, pilotId, montgolfiereId, temp }) {
		const flight = Flights.findOne(_id);
		const pilot = Meteor.users.findOne(pilotId);
		const montgolfiere = Montgolfieres.findOne(montgolfiereId);
		const reservations = Reservations.find({_id: { $in: reservationIds }}).fetch();
		const passengersCount = _.reduce(reservations, (count, reservation) => {
			return count + reservation.passengers.length;
		}, 0);
		const weightCount = _.reduce(reservations, (count, reservation) => {
			return count + reservation.totalWeight();
		}, 0) + (pilot && pilot.profile.weight ? pilot.profile.weight : 0 );
		const maxWeight = temp ? montgolfiere.maxWeight["temp" + temp] : montgolfiere.defaultWeight;

		/*if (flight.status !== "preparing")
			throw new Meteor.Error('already_prepared',
										'Vous ne pouvez plus modifier les informations de ce vol');*/

		if (!reservations.length)
			throw new Meteor.Error('need_passengers',
										'Il n\'y aucun passagers pour ce vol');

		if (passengersCount > montgolfiere.maxPassengers)
			throw new Meteor.Error('too_much_passengers',
										'Il n\'y pas suffisament de place dans la montgolfiere');

		if (weightCount > maxWeight)
			throw new Meteor.Error('too_much_weight',
										'La montgolfiere ne peut pas supporter une telle charge');

		if (Flights.findOne({ _id: { $ne: _id }, period: flight.period, date: flight.date, montgolfiereId: montgolfiereId, status: {$ne: 'cancelled' }}))
			throw new Meteor.Error('montgolfiere_already_used',
										'La montgolfiere est déjà reservée pour un autre vol');

		if (!montgolfiere.allowedPilots.includes(pilotId))
			throw new Meteor.Error('not_allowed',
										'Vous ne pouvez pas piloter cette montgolfiere');

		if (pilotId !== Meteor.userId() && !Roles.userHasPermission(Meteor.userId(), 'flights.organize'))
			throw new Meteor.Error('not_allowed',
								   'Vous ne pouvez pas preparer un vol pour un autre pilote');

		if (Flights.findOne({ _id: { $ne: _id }, period: flight.period, date: flight.date, reservationIds: { $in: reservationIds }}))
			throw new Meteor.Error('reservations_already_used',
										'Les réservations choisies sont déjà programmés sur un autre vol');

		Flights.update(_id, {
			$set: {
				temp,
				place,
				reservationIds,
				pilotId,
				montgolfiereId,
				validToFlight: true
			}
		});
	}
});

export const confirmFlight = new ValidatedMethod({
	name: 'flights.confirm',
	validate: new SimpleSchema({
		_id: { type: String },
		temp: { type: Number },
		placeId: { type: String },
		meetingDate: { type: Date },
	}).validator(),
	run({ _id, temp, placeId, meetingDate }) {
		const flight = Flights.findOne(_id);
		const pilot = Meteor.users.findOne(flight.pilotId);

		if (!flight.validToFlight)
			throw new Meteor.Error('not_valid',
										'Veuillez renseigner correctement les informations de ce vol avant le valider.');

		const availableTotalWeight = flight.getMontgolfiere().maxWeight["temp" + temp];
		const scheduledPassengersWeight = _.reduce(flight.getReservations(), (weight, reservation) => {
			return weight + reservation.totalWeight();
		}, 0) + (pilot && pilot.profile.weight ? pilot.profile.weight : 0);
		if (scheduledPassengersWeight > availableTotalWeight)
			throw new Meteor.Error('too_much_weight',
										'La montgolfiere ne peut pas supporter une telle charge à cette température');

		if (Meteor.userId() !== flight.pilotId)
			throw new Meteor.Error('not_allowed',
										'Vous devez être le pilote de ce vol pour pouvoir le préparer');

		Flights.update(_id, {
			$set: {
				temp,
				placeId,
				meetingDate,
				status: "prepared"
			}
		});

		let userId = Meteor.userId();
		_.each(flight.getReservations(), (reservation) => {
			// History :
			let historyItem = {
				type: "confirmed",
				date: new Date(),
				scheduledAt: flight.date,
				userId
			};

			Reservations.update(reservation._id, {
				$push: {
					history: historyItem
				}
			});
		});



		if (flight.status == "preparing" && Meteor.isServer) {
			SmsManager.notifyProgrammation(flight.getReservations(), meetingDate, flight.getPilote(), Places.findOne(placeId));
		}
	}
})

export const cancelFlight = new ValidatedMethod({
	name: 'flights.cancel',
	validate: new SimpleSchema({
		_id: { type: String },
		reason: { type: String }
	}).validator(),
	run({ _id, reason }) {
		const flight = Flights.findOne(_id);
		const pilot = Meteor.users.findOne(flight.pilotId);

		if (Meteor.userId() !== flight.pilotId)
			throw new Meteor.Error('not_allowed',
										'Vous devez être le pilote de ce vol pour pouvoir l\'annuler');

		Flights.update(_id, {
			$set: {
				status: "cancelled"
			}
		});

		let userId = Meteor.userId();
		_.each(flight.getReservations(), (reservation) => {
			let historyItem = {
				type: "cancelled",
				date: new Date(),
				scheduledAt: flight.date,
				userId
			};

			Reservations.update(reservation._id, {
				$push: {
					history: historyItem
				},
				$set: {
					scheduledAt: null
				}
			});
		});

		if (Meteor.isServer)
			SmsManager.cancelProgrammation(flight.getReservations(), flight.date, reason, pilot);
	}
});

export const encloseFlight = new ValidatedMethod({
	name: 'flights.enclose',
	validate: new SimpleSchema({
		_id: { type: String },
		takeOffDate: { type: Date },
		landingDate: { type: Date },
		takeOffPlace: { type: String },
		landingPlace: { type: String },
		montgolfiereId: {type: String },
		pilotId: {type: String },
	}).validator(),
	run({ _id, takeOffDate, landingDate, takeOffPlace, landingPlace, montgolfiereId, pilotId }) {
		const flight = Flights.findOne(_id);

		Roles.checkPermission(Meteor.userId(), "flights.enclose", Meteor.userId(), flight);

		if (moment(takeOffDate).isAfter(landingDate))
			throw new Meteor.Error('wrong_time',
										"L'heure d'atterissage doit être après l'heure de décollage");

		console.log('montgolfiereId', montgolfiereId);
		console.log('pilotId', pilotId);

		_.each(flight.getReservations(), (reservation) => {
			reservation.consume();
		});

		let userId = Meteor.userId();
		_.each(flight.getReservations(), (reservation) => {
			let historyItem = {
				type: "consumed",
				date: new Date(),
				scheduledAt: flight.date,
				userId
			};

			Reservations.update(reservation._id, {
				$push: {
					history: historyItem
				}
			});
		});

		Flights.update(_id, {
			$set: {
				status: "validated",
				landingDate,
				takeOffDate,
				landingPlace,
				takeOffPlace,
				montgolfiereId,
				pilotId
			}
		});
	}
})

// Get list of all method names on Lists
const LISTS_METHODS = _.map([
	prepareFlight,
	confirmFlight,
	cancelFlight,
	encloseFlight,
], 'name');

if (Meteor.isServer) {
  // Only allow 5 list operations per connection per second
  DDPRateLimiter.addRule({
    name(name) {
      return _.includes(LISTS_METHODS, name);
    },

    // Rate limit per connection ID
    connectionId() { return true; },
  }, 5, 1000);
}



/** WEBPACK FOOTER **
 ** ./src/universal/collections/flights/methods.js
 **/